import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { authService, type AuthState, type User } from "@/services/AuthService";
import { processReferralConversion } from "@/hooks/useReferralCapture";
import { clearSessionLocation } from "@/hooks/useSessionLocation";
import { Bugsnag } from "@/lib/bugsnag";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { clearPermissionsCache } from "@/lib/permissionsCache";

const bugsnagEnabled = !!import.meta.env.VITE_BUGSNAG_API_KEY;

/**
 * Removes all `hashtag_compare_tag_*` keys from localStorage.
 * Called on sign-out so stale compare-tag entries from this (and any
 * previously-active) restaurant do not accumulate across account switches.
 */
function clearCompareTagStorage(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith("hashtag_compare_tag_")) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));
}

/**
 * Canonical set of localStorage/sessionStorage keys cleared on every sign-out,
 * whether voluntary (logout()) or forced (auth:expired event / handleExpired).
 * Both paths MUST call this function so stale partner-session data is never
 * left behind after a forced expiry.
 *
 * Keys in this list (2025-06):
 *   sessionStorage — partnerEatzyToken, adminEatzyToken
 *   localStorage   — partnerRestaurantId, partnerRestaurantName,
 *                    partnerRestaurantProfile, partnerRestaurantRole,
 *                    partnerRestaurantCuisineType, partnerKudozRestaurantUuid,
 *                    partnerUserId, guestContactEmail
 */
function clearSessionArtifacts(): void {
  clearSessionLocation();
  clearPermissionsCache();
  clearCompareTagStorage();
  queryClient.clear();
  sessionStorage.removeItem("partnerEatzyToken");
  sessionStorage.removeItem("adminEatzyToken");
  localStorage.removeItem("partnerRestaurantId");
  localStorage.removeItem("partnerRestaurantName");
  localStorage.removeItem("partnerRestaurantProfile");
  localStorage.removeItem("partnerRestaurantRole");
  localStorage.removeItem("partnerRestaurantCuisineType");
  localStorage.removeItem("partnerKudozRestaurantUuid");
  localStorage.removeItem("partnerUserId");
  localStorage.removeItem("guestContactEmail");
}

function setBugsnagUser(user: User | null) {
  if (!bugsnagEnabled) return;
  try {
    if (user) {
      Bugsnag.setUser(String(user.id), user.email, user.name || user.email);
    } else {
      Bugsnag.setUser(undefined, undefined, undefined);
    }
  } catch {
    // Never let Bugsnag crash auth flow
  }
}

export interface EatzyUser {
  id: number | string;
  canonicalId?: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  phone?: string;
  avatar?: string;
  bio?: string;
  displayName?: string;
  kudozBalance?: number;
  lifetimeKudozEarned?: number;
  accountBalance?: string;
  trustScore?: number;
  trustTier?: "newbie" | "reliable" | "trusted" | "elite";
  totalReviews?: number;
  totalOrders?: number;
  profileCompletionPercent?: number;
}

export interface GuestLoginData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface AuthContextType {
  user: EatzyUser | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isGuestAccount: boolean;
  guestContactEmail: string | null;
  login: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; error?: string }>;
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    inviteAttribution?: { inviteToken?: string }
  ) => Promise<{ success: boolean; error?: string }>;
  loginAsGuest: (
    data: GuestLoginData
  ) => Promise<{ success: boolean; error?: string; errorCode?: string }>;
  loginWithProvider: (
    provider: "google" | "apple" | "facebook",
    returnUrl?: string
  ) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  signInWithGoogle: () => void;
  signInWithFacebook: () => void;
  signInWithApple: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Helper function to convert User to EatzyUser
const convertToEatzyUser = (user: User | null): EatzyUser | null => {
  if (!user) return null;

  // Use firstName/lastName from user if available, otherwise parse from name
  const firstName = user.firstName || user.name?.split(" ")[0] || "";
  const lastName =
    user.lastName || user.name?.split(" ").slice(1).join(" ") || "";

  return {
    id: user.id,
    canonicalId: user.canonicalId,
    email: user.email,
    firstName,
    lastName,
    name: user.name || `${firstName} ${lastName}`.trim(),
    phone: user.phone,
    avatar: user.avatar,
    bio: user.bio,
    displayName: user.displayName,
    kudozBalance: user.kudozBalance,
    lifetimeKudozEarned: user.lifetimeKudozEarned,
    accountBalance: user.accountBalance,
    trustScore: user.trustScore,
    trustTier: user.trustTier,
    totalReviews: user.totalReviews,
    totalOrders: user.totalOrders,
    profileCompletionPercent: user.profileCompletionPercent,
  };
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>(() => ({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  }));

  const [hasInitialized, setHasInitialized] = useState(false);
  const { toast } = useToast();
  const isAuthenticatedRef = useRef(false);

  // Keep ref in sync with state for use in event listeners (avoids stale closure)
  useEffect(() => {
    isAuthenticatedRef.current = authState.isAuthenticated;
  }, [authState.isAuthenticated]);

  // Track the previously-seen user id so we can distinguish a real account
  // switch from the initial boot-time null → id transition.
  // undefined = "we haven't seen any value yet" (before the first render)
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  // Whenever the authenticated user changes (login, logout, account switch),
  // wipe the permissions cache so a previous user's roles can never be served
  // to the next session in the same tab.
  //
  // IMPORTANT: We intentionally skip the very first null → id transition that
  // happens on every fresh page load (auth init). Calling removeQueries at that
  // moment races with the in-flight permissions fetch started by RequirePermission
  // and can leave the query observer in a "no data, not fetching" limbo that
  // shows the spinner forever.
  //
  // We use removeQueries (hard wipe) for real user switches so that the old
  // user's permission data is never accessible to the new session. This is
  // safe for switches because RequirePermission's query is re-enabled fresh
  // after the user/token change, triggering a new fetch automatically.
  useEffect(() => {
    const prevId = prevUserIdRef.current;
    const currId = authState.user?.id ?? null;
    prevUserIdRef.current = currId;

    // prevId === undefined means this is the very first render — skip it.
    // We only need to act when a previously-established user session changes.
    if (prevId === undefined) return;

    // Real switch: previous user id was non-null and differs from the new one
    // (covers login-as-different-user AND logout null). Use removeQueries so
    // the old user's permissions are wiped from cache immediately — stale data
    // must never be rendered for a new session.
    //
    // This is safe to call here (unlike the boot transition) because:
    // - RequirePermission only enables its query when `isAuthenticated && !!token`
    // - During a user switch both values momentarily drop or change, causing
    //   the query to re-enable fresh after the cache is cleared.
    if (prevId !== null && prevId !== currId) {
      // Wipe the entire React Query cache so no query from the previous
      // session can bleed into the new one — including any user-scoped
      // keys added in the future without a corresponding removeQueries
      // call here. RequirePermission re-enables its query fresh after
      // the token/user change, so this is safe to call on every real
      // user switch or logout.
      queryClient.clear();
      // Also wipe the persistent permissions snapshot so a previous user's
      // roles can never bleed into the next session even if the new
      // session immediately hits a permissions outage.
      clearPermissionsCache();
    } else if (prevId === null && currId !== null) {
      // Fresh login transition (null → user) WITHIN AN ALREADY-MOUNTED
      // session — i.e. the user submitted the login form in this same
      // tab. Note this branch does NOT fire on a hard refresh of an
      // already-authenticated session: a hard refresh re-mounts the
      // provider so prevId starts as `undefined`, which the early
      // return above catches before we reach this branch. That means
      // cache continuity across page reloads is preserved (a
      // permissions outage immediately after a refresh will still
      // serve the persisted snapshot), and we only wipe when the
      // user-identity transitions live in-tab.
      //
      // The per-user check inside getFreshPermissionsCache already
      // prevents serving cross-user entries, but wiping the slot on
      // every in-tab fresh login gives strict requirement parity and
      // protects against any leftover snapshot from a prior tab
      // session that may have skipped the explicit logout path
      // (e.g. a hard tab-close that bypassed the logout handler).
      clearPermissionsCache();
    }
  }, [authState.user?.id]);

  // Listen for expired-token events emitted by queryClient and auto-logout
  useEffect(() => {
    const handleExpired = () => {
      if (!isAuthenticatedRef.current) return;
      const currentPath = window.location.pathname + window.location.search;
      // Route partner sessions back to the partner login so they don't land on
      // the consumer login form. Admin and consumer flows share /login today.
      const isPartner = window.location.pathname.startsWith("/partner");
      const loginPath = isPartner ? "/partner/login" : "/login";
      const skipReturn = window.location.pathname === loginPath;
      const redirectParam = skipReturn
        ? ""
        : `?returnUrl=${encodeURIComponent(currentPath)}`;
      authService
        .signOut()
        .catch(() => {})
        .finally(() => {
          clearSessionArtifacts();
          setAuthState({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
          setBugsnagUser(null);
          toast({
            title: "Session expired",
            description: "Please log in again to continue.",
            variant: "destructive",
          });
          window.location.href = `${loginPath}${redirectParam}`;
        });
    };
    window.addEventListener("auth:expired", handleExpired);
    return () => window.removeEventListener("auth:expired", handleExpired);
  }, [toast]);

  // Proactively keep tokens fresh in the background. We can't know the Kudoz
  // JWT expiry, so we refresh periodically on a coarse timer; for the Eatzy
  // access token we use its known TTL and refresh ~5 min before expiry. We
  // also refresh on focus/visibility-change so a user returning to a tab that
  // sat idle past expiry gets a fresh token before their next click.
  useEffect(() => {
    const KUDOZ_INTERVAL_MS = 30 * 60 * 1000; // 30 min
    const EATZY_LEAD_SECONDS = 5 * 60; // refresh 5 min before expiry
    const EATZY_CHECK_INTERVAL_MS = 60 * 1000; // re-check every minute

    let cancelled = false;

    const refreshIfAuthed = async () => {
      if (cancelled || !isAuthenticatedRef.current) return;
      // Best-effort: ignore failures here — the reactive layer will catch any
      // hard expiry on the next API call and dispatch auth:expired.
      try {
        await authService.refreshKudozToken();
      } catch {
        /* swallow */
      }
    };

    const refreshEatzyIfNearExpiry = async () => {
      if (cancelled || !isAuthenticatedRef.current) return;
      if (!authService.hasEatzyRefreshToken()) return;
      const secs = authService.getEatzyTokenSecondsUntilExpiry();
      if (secs === null || secs > EATZY_LEAD_SECONDS) return;
      try {
        await authService.refreshEatzyToken();
      } catch {
        /* swallow */
      }
    };

    const kudozTimer = window.setInterval(refreshIfAuthed, KUDOZ_INTERVAL_MS);
    const eatzyTimer = window.setInterval(
      refreshEatzyIfNearExpiry,
      EATZY_CHECK_INTERVAL_MS
    );

    const handleFocus = () => {
      void refreshIfAuthed();
      void refreshEatzyIfNearExpiry();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") handleFocus();
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(kudozTimer);
      window.clearInterval(eatzyTimer);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    if (hasInitialized) return;

    const initAuth = async () => {
      console.log("🔄 useAuth: Initializing auth state...");
      const storedState = authService.getAuthState();

      if (storedState.token) {
        console.log("🔍 useAuth: Verifying stored token...");
        const user = await authService.verifyToken();
        if (user) {
          console.log("✅ useAuth: Token valid, setting authenticated state");
          // Only persist enriched user if trust fields are present (ensures we don't overwrite with incomplete data)
          if (user.trustScore !== undefined) {
            authService.updateStoredUser(user);
          }
          setAuthState({
            user,
            token: storedState.token,
            isAuthenticated: true,
            isLoading: false,
          });
          setBugsnagUser(user);
          setHasInitialized(true);
          return;
        } else {
          console.log("❌ useAuth: Token invalid, clearing stored auth");
          await authService.signOut();
        }
      }
      // No valid auth found
      console.log(
        "🚫 useAuth: No valid auth found, setting unauthenticated state"
      );
      setAuthState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      setHasInitialized(true);
    };

    initAuth();
  }, [hasInitialized]);

  const login = async (email: string, password: string) => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true }));

      const { token, user } = await authService.signIn(email, password);

      setAuthState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
      setBugsnagUser(user);

      await processReferralConversion();

      return { success: true };
    } catch (error: unknown) {
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      console.error("[useAuth] Login error:", error);
      return {
        success: false,
        error:
          (error instanceof Error ? error.message : String(error)) ||
          "Login failed",
      };
    }
  };

  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    inviteAttribution?: { inviteToken?: string }
  ) => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true }));

      const { token, user } = await authService.signUp(
        email,
        password,
        firstName,
        lastName,
        inviteAttribution
      );

      setAuthState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
      setBugsnagUser(user);

      await processReferralConversion();

      return { success: true };
    } catch (error: unknown) {
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      console.error("[useAuth] Sign up error:", error);
      return {
        success: false,
        error:
          (error instanceof Error ? error.message : String(error)) ||
          "Sign up failed",
      };
    }
  };

  const loginAsGuest = async (
    data: GuestLoginData
  ): Promise<{ success: boolean; error?: string; errorCode?: string }> => {
    try {
      const response = await fetch("/api/auth/guest/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const body = await response.json();
      if (!response.ok) {
        return {
          success: false,
          error: body.message || body.error || "Could not create guest session",
          errorCode: body.error || undefined,
        };
      }
      if (
        !body.token ||
        typeof body.token !== "string" ||
        !body.user ||
        !body.user.id
      ) {
        console.error(
          "[useAuth] loginAsGuest: malformed response payload",
          body
        );
        return {
          success: false,
          error: "Unexpected response from server. Please try again.",
        };
      }
      if (body.eatzyAccessToken) {
        sessionStorage.setItem("adminEatzyToken", body.eatzyAccessToken);
        sessionStorage.setItem("partnerEatzyToken", body.eatzyAccessToken);
        localStorage.setItem("eatzyUserToken", body.eatzyAccessToken);
        if (body.eatzyRefreshToken)
          localStorage.setItem("eatzyRefreshToken", body.eatzyRefreshToken);
        if (body.eatzyTokenExpires)
          localStorage.setItem(
            "eatzyTokenExpires",
            String(body.eatzyTokenExpires)
          );
      }
      const guestUser: User = {
        id: body.user.id ?? "",
        email: body.user.email ?? "",
        name:
          body.user.name ??
          `${body.user.firstName ?? ""} ${body.user.lastName ?? ""}`.trim(),
        avatar: body.user.avatar,
        bio: body.user.bio,
        displayName: body.user.displayName,
        firstName: body.user.firstName,
        lastName: body.user.lastName,
        phone: body.user.phone,
        kudozBalance: body.user.kudozBalance,
        lifetimeKudozEarned: body.user.lifetimeKudozEarned,
        accountBalance: body.user.accountBalance,
        trustScore: body.user.trustScore,
        trustTier: body.user.trustTier,
        totalReviews: body.user.totalReviews,
        totalOrders: body.user.totalOrders,
        profileCompletionPercent: body.user.profileCompletionPercent,
        followersCount: body.user.followersCount,
        followingCount: body.user.followingCount,
        isGuestAccount: true,
      };
      localStorage.setItem("auth_token", body.token);
      localStorage.setItem("auth_user", JSON.stringify(guestUser));
      if (body.guestContactEmail) {
        localStorage.setItem("guestContactEmail", body.guestContactEmail);
      }
      setAuthState({
        user: guestUser,
        token: body.token,
        isAuthenticated: true,
        isLoading: false,
      });
      setBugsnagUser(guestUser);
      await processReferralConversion();
      return { success: true };
    } catch (error: unknown) {
      console.error("[useAuth] loginAsGuest error:", error);
      return {
        success: false,
        error:
          (error instanceof Error ? error.message : String(error)) ||
          "Guest sign-in failed",
      };
    }
  };

  const loginWithProvider = (
    provider: "google" | "apple" | "facebook",
    returnUrl: string = "/"
  ) => {
    // Use the consistent /api/auth/{provider} flow. `returnUrl` is
    // stored in sessionStorage so `/auth/callback` can forward the user
    // to it after token finalization (used for non-invite deep links).
    //
    // For partner-invite paths (Task #840) we ALSO forward
    // `?returnTo=/invite/<token>` so the server can ask the Auth Hub to
    // post the session token straight back to the invite page —
    // `AcceptInvite.tsx` finalizes the token there. The server enforces
    // the same strict invite-path regex before honouring the override.
    if (returnUrl && returnUrl !== "/") {
      sessionStorage.setItem("auth_return_url", returnUrl);
    }
    const isInvitePath = /^\/invite\/[A-Za-z0-9_-]{16,}$/.test(returnUrl);
    const qs = isInvitePath ? `?returnTo=${encodeURIComponent(returnUrl)}` : "";
    window.location.href = `/api/auth/${provider}${qs}`;
  };

  const logout = async () => {
    try {
      await authService.signOut();
    } catch (error) {
      console.error("[useAuth] Logout error:", error);
    } finally {
      clearSessionArtifacts();
      setAuthState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      setBugsnagUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const storedState = authService.getAuthState();
      const user = await authService.verifyToken();
      if (user) {
        // Only persist enriched user if trust fields are present
        if (user.trustScore !== undefined) {
          authService.updateStoredUser(user);
        }
        setAuthState((prev) => ({
          ...prev,
          user,
          token: storedState.token ?? prev.token,
          isAuthenticated: true,
        }));
      }
    } catch (error) {
      console.error("[useAuth] Refresh user error:", error);
    }
  };

  const signInWithGoogle = () => {
    console.log("[useAuth] Initiating Google sign in...");
    authService.signInWithGoogle();
  };

  const signInWithFacebook = () => {
    console.log("[useAuth] Initiating Facebook sign in...");
    authService.signInWithFacebook();
  };

  const signInWithApple = () => {
    console.log("[useAuth] Initiating Apple sign in...");
    authService.signInWithApple();
  };

  const eatzyUser = convertToEatzyUser(authState.user);
  const isGuestAccount = authState.user?.isGuestAccount === true;
  const guestContactEmail = isGuestAccount
    ? (localStorage.getItem("guestContactEmail") ?? null)
    : null;

  return (
    <AuthContext.Provider
      value={{
        user: eatzyUser,
        token: authState.token,
        isLoading: authState.isLoading,
        isAuthenticated: authState.isAuthenticated,
        isGuestAccount,
        guestContactEmail,
        login,
        signUp,
        loginAsGuest,
        loginWithProvider,
        logout,
        refreshUser,
        signInWithGoogle,
        signInWithFacebook,
        signInWithApple,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
